import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, Paper, Typography, Tabs, Tab } from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useAnalyticContext } from '../../../../hooks/analyticContext';
import { configureBhrtcChart } from '../../../ChartConfiguration/ChartConfiguration';
import TopTenMembers from '../../../../Models/Claims/TopTenMembers.model';
import FilterOptions from '../DetailFilterOption';
import { getBhRfCostGraph, getBfCostGraph, getTopTenBhRtcAndRfCostMembers } from '../AnalyticService';
import { ClaimAnalyticEnum } from '../../../../Enum/ClaimAnalyticEnum';
import { moneyFormatter } from '../../../../utils/moneyFormat';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

const BhRtcCostDetail = () => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isMemberShow, setIsMemberShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [monthChartOptions, setMonthChartOptions] = useState({});
    const [bhRtcGraphSummary, setBhRtcGraphSummary] = useState<any>([]);
    const [topTenMembers, setTopTenMembers] = useState<TopTenMembers[]>([]);
    const [selectedYear, setSelectedYear] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });
    const chartHeight = 380;

    const {
        costSummaryHealthPlanIds,
        setCostSummaryHealthPlanIds,
        costSummaryYears,
        setCostSummaryYears,
        costSummaryMonths,
        setCostSummaryMonths,
        costSummaryTeam,
        setCostSummaryTeam,
        showCostSummaryInActiveTeams,
        setShowCostSummaryInActiveTeams,
    } = useAnalyticContext();

    const bhRtcHealthPlanIds = costSummaryHealthPlanIds;
    const setBhRtcHealthPlanIds = setCostSummaryHealthPlanIds;
    const bhRtcYears = costSummaryYears;
    const setBhRtcYears = setCostSummaryYears;
    const bhRtcMonths = costSummaryMonths;
    const setBhRtcMonths = setCostSummaryMonths;
    const bhRtcTeam = costSummaryTeam;
    const setBhRtcTeam = setCostSummaryTeam;
    const showBhRtcInActiveTeams = showCostSummaryInActiveTeams;
    const setShowBhRtcInActiveTeams = setShowCostSummaryInActiveTeams;
    const bhRtcName = 'BH RTC';
    const bhRfName = 'BH RF';

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setMonthChartOptions(
                configureBhrtcChart('BH RTC/RF Costs', '', '', 'Cost', [{ name: '', data: [0], showInLegend: false, title: { text: null } }], [0], '', history, chartHeight)
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow, history]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    const changeFilter = async () => {
        if (bhRtcHealthPlanIds && bhRtcHealthPlanIds.length > 0) {
            setIsGraphShow(false);
            setIsMemberShow(false);
            let response = [];
            let bhrtc, bhrf;
            const bfrfCost = await getBhRfCostGraph(
                bhRtcHealthPlanIds,
                bhRtcTeam,
                bhRtcYears,
                bhRtcMonths,
                showBhRtcInActiveTeams
            );
            if (Boolean(bfrfCost)) {
                bhrf = bfrfCost;
                bhrf.Name = bhRfName;
                response.push(bhrf);
            }
            const bhrtcCost = await getBfCostGraph(
                bhRtcHealthPlanIds,
                bhRtcTeam,
                bhRtcYears,
                bhRtcMonths,
                showBhRtcInActiveTeams
            );
            if (Boolean(bhrtcCost)) {
                bhrtc = bhrtcCost;
                bhrtc.Name = bhRtcName;
                response.push(bhrtc);
            }
            setBhRtcGraphSummary(response);
            setIsPrepared(true);
            setIsGraphShow(true);

            const members = await getTopTenBhRtcAndRfCostMembers(
                bhRtcHealthPlanIds,
                bhRtcTeam,
                bhRtcYears,
                bhRtcMonths,
                showBhRtcInActiveTeams
            );
            setTopTenMembers(members);
            setIsMemberShow(true);
        }
    };

    useEffect(() => {
        if (!isFetched && bhRtcHealthPlanIds && bhRtcHealthPlanIds.length > 0) {
            changeFilter();
            setIsFetched(true);
        }
        initialPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps;
    }, [bhRtcHealthPlanIds]);

    useEffect(() => {
        if (bhRtcGraphSummary && isPrepared) {
            let xAxisValues = [];
            let yAxisValues = [];
            let isData = false;

            bhRtcGraphSummary.forEach((bhrtcData) => {
                let yValues = [];
                bhrtcData.forEach((bhrtc) => {
                    if (bhrtc.TotalCost && bhrtc.TotalCost > 0) {
                        isData = true;
                    }

                    yValues.push({
                        y: bhrtc.TotalCost,
                        name: bhrtc.Category,
                        title: bhrtc.Name
                    });
                });
                yAxisValues.push({
                    name: bhrtcData.Name,
                    data: yValues,
                    pointPadding: 0,
                    color: bhrtcData.Name === bhRtcName ? '#917DFF' : '#B0DBDE',
                    showInLegend: true,
                });
            });

            xAxisValues = isData
                ? (bhRtcGraphSummary?.length > 1
                    ? bhRtcGraphSummary[1]?.map(x => x.Category)
                    : bhRtcGraphSummary[0]?.map(x => x.Category)) || []
                : [];

            setMonthChartOptions(
                configureBhrtcChart(
                    'BH RTC/RF Costs',
                    '',
                    '',
                    'Cost',
                    yAxisValues,
                    xAxisValues,
                    'BhRtcCost',
                    history,
                    chartHeight
                ));
            setIsGraphShow(true);
            setIsPrepared(false);
        }
    }, [bhRtcGraphSummary, bhRtcHealthPlanIds, isPrepared, history]);

    const columns: GridColDef[] = [
        {
            field: 'Rank',
            headerName: '#',
            maxWidth: 70,
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'Cost',
            headerName: 'Cost',
            flex: 1,
            valueGetter: (value, row) => `${moneyFormatter.format(row.Cost)}`,
        },
    ];

    const navigateToGraph = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    return (
        <Paper elevation={5}>
            <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                <Grid size={12} paddingBottom={1}>
                    <span onClick={navigateToGraph}>
                        <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                    </span>
                    <Typography display={'inline-block'} variant="h6" color={'primary'}>
                        BH RTC/RF Costs
                    </Typography>
                </Grid>
                <FilterOptions
                    healthPlanIds={bhRtcHealthPlanIds}
                    setHealthPlanIds={setBhRtcHealthPlanIds}
                    years={bhRtcYears}
                    setYears={setBhRtcYears}
                    months={bhRtcMonths}
                    setMonths={setBhRtcMonths}
                    team={bhRtcTeam}
                    setTeam={setBhRtcTeam}
                    showInActiveTeams={showBhRtcInActiveTeams}
                    setShowInActiveTeams={setShowBhRtcInActiveTeams}
                    onFetch={changeFilter}
                    claimType={ClaimAnalyticEnum.BhRtcCost}
                />
                <Grid size={5} marginTop={1} sx={{ position: 'relative' }}>
                    {!isGraphShow ? (
                        <Box className="loader-center">
                            <CircularProgress />
                        </Box>
                    ) : null}
                    <Grid container mb={2}>
                        <Grid size={12} marginTop={2} style={{ backgroundColor: '#F5F5F5' }} paddingY={1} paddingLeft={2} paddingRight={2}>
                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                BH RTC/RF Costs
                            </Typography>
                        </Grid>
                    </Grid>
                    <HighchartsReact highcharts={Highcharts} options={monthChartOptions} />
                </Grid>
                <Grid size={1}></Grid>
                <Grid size={6} marginTop={1}>
                    <Grid container>
                        <Grid size={12} marginTop={2} style={{ backgroundColor: '#F5F5F5' }} paddingY={1} paddingLeft={2} paddingRight={2}>
                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                Top 10 Members
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2}>
                        <Grid size={12} marginTop={2} minHeight="40vh" sx={{ position: 'relative' }}>
                            {!isGraphShow || !isMemberShow ? (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    <Tabs
                                        value={selectedYear}
                                        onChange={(e, newValue) => setSelectedYear(newValue)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                    >
                                        {topTenMembers?.map((detail, index) => (
                                            <Tab key={index} label={detail.Year === '0' ? "Baseline" : detail.Year} />
                                        ))}
                                    </Tabs>
                                    <Grid spacing={2}>
                                        {topTenMembers?.map((detail, index) => {
                                            const rowsWithRank = detail.Members.map((m, i) => ({
                                                ...m,
                                                Rank: i + 1,
                                            }));
                                            return (
                                                selectedYear === index && (
                                                    <Grid container key={`detail-${index}`}>
                                                        <Grid size={12} sx={{ height: '55vh', width: '100%' }}>
                                                            <div style={{ width: '100%', height: '55vh', overflow: 'auto' }}>
                                                                <DataGrid
                                                                    rows={rowsWithRank}
                                                                    columns={columns}
                                                                    paginationModel={paginationModel}
                                                                    onPaginationModelChange={setPaginationModel}
                                                                    pageSizeOptions={[5]}
                                                                    getRowId={(row) => row.Id + row.AhcccsId}
                                                                    getRowHeight={() => 45}
                                                                    autoHeight={false}
                                                                    sx={{
                                                                        height: '100%',
                                                                        [`& .MuiDataGrid-cell`]: { py: 1 },
                                                                    }}
                                                                />
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                )
                                            )
                                        })}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default BhRtcCostDetail;
